/**
 * App root: query client + router + theme. The route table (and the ⌘K
 * palette, which needs router context) lives in routes.tsx; server state in
 * api/queries.ts; canvas/chat/ui state in stores/.
 */

import { QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { RouterProvider } from 'react-router/dom';

import { makeQueryClient, useHealth } from './api/queries';
import { startErrorReporting } from './lib/errorReporting';
import { createRouter } from './routes';
import { applyThemeAttribute, useUiStore } from './stores/uiStore';

/**
 * Error reporting starts when the server says it has a DSN for the browser
 * (GET /api/health: null on a local or unconfigured deploy, so nothing is
 * loaded there). A child of the provider because the health query needs
 * the client; renders nothing. lib/errorReporting has the rest.
 */
function ErrorReportingWiring() {
  const health = useHealth().data;

  useEffect(() => {
    if (health === undefined) return;
    void startErrorReporting(health.sentry_dsn, {
      environment: health.hosted ? 'hosted' : 'local',
      // The SPA ships inside the server image: its build IS the server's version.
      release: `loremap-web@${health.version}`,
    });
  }, [health]);

  return null;
}

export default function App() {
  const [queryClient] = useState(makeQueryClient);
  const [router] = useState(createRouter);
  const theme = useUiStore((s) => s.theme);

  useEffect(() => {
    applyThemeAttribute(theme);
  }, [theme]);

  return (
    <QueryClientProvider client={queryClient}>
      <ErrorReportingWiring />
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}
